import { HTMLReactParserOptions } from 'html-react-parser';
import { parseHtmlWithMathLatex } from './htmlParser';
import { getMathMlTagNames } from './mathml';

// Tags that appear in authored vignette copy. Deliberately a whitelist: see
// renderVignetteContent for why "looks like a tag" is not good enough. The
// MathML names come from the same set the parser uses, so the two can't drift.
const CONTENT_TAGS = [
  'p',
  'br',
  'hr',
  'b',
  'i',
  'u',
  's',
  'em',
  'strong',
  'sup',
  'sub',
  'span',
  'div',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'img',
  'svg',
  'a',
  'h[1-6]',
  'small',
  'code',
  'pre',
  'blockquote',
  'figure',
  'figcaption',
  ...getMathMlTagNames().map((tag) => tag.replace('-', '\\-')),
];

const HTML_TAG_RE = new RegExp(
  `<\\/?(?:${CONTENT_TAGS.join('|')})\\b[^>]*>`,
  'i'
);

// Content that is worth showing even though it has no text of its own.
const MEDIA_TAG_RE = /<(?:img|svg|video|iframe|table|math)\b/i;

const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Renders vignette copy that may or may not be HTML.
 *
 * The chat backend now sends authored HTML, but turns persisted before it did
 * carry plain text that had already been through `stripHtml` — which decodes
 * `&lt;` back into a bare `<`. Handing that to an HTML parser is lossy: a stem
 * reading "an ejection fraction <normal for her age" parses `<normal ...>` as a
 * tag and silently swallows the rest of the sentence. Lab values and
 * comparisons make that shape common in question stems.
 *
 * So: parse content that carries a recognised tag, and escape everything else
 * before parsing, which keeps LaTeX handling while rendering the text verbatim.
 *
 * Note this is a whole-string decision. Copy that mixes real markup with a bare
 * `<` still takes the parse path and still loses the tail — the same exposure
 * QBank has always had on the same content.
 */
export const renderVignetteContent = (
  value: string,
  parserOptions?: HTMLReactParserOptions
) =>
  parseHtmlWithMathLatex(
    HTML_TAG_RE.test(value) ? value : escapeHtml(value),
    parserOptions
  );

/**
 * Whether a field has anything worth rendering. `<p></p>` and `&nbsp;` are
 * truthy strings but produce an empty block on screen; an image or a table has
 * no text of its own but is very much worth showing.
 */
export const hasVignetteContent = (value: string) =>
  MEDIA_TAG_RE.test(value) ||
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim().length > 0;
